import { Button, Icon, LoadingDots } from '@advanza/ui'
import clsx from 'clsx'
import { useToast } from 'components/misc/Toaster'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import style from './safeButton.module.css'

/** @typedef {import('react').ReactNode} ReactNode */

/**
 * @param {object} props
 * @param {() => unknown} props.action
 * @param {ReactNode} props.buttonText
 * @param {ReactNode | false} [props.cancelText=false]
 * @param {string} [props.confirmText='Confirm']
 * @param {ReactNode} [props.confirmMessage] Defaults to `confirmText` in bold red.
 * @param {ReactNode} [props.successMessage] Shown after `action` succeeds; falls back to 'Success!' when falsy.
 * @param {boolean} [props.hidden=false]
 * @param {(response: unknown) => void} [props.callback]
 * @param {string} [props.tagName='div']
 * @param {boolean} [props.hideOnFinish=false]
 * @param {boolean} [props.newStyle=false]
 * @param {boolean} [props.textButton=false]
 * @param {string} [props.className]
 */
const SafeButton = ({
    action,
    buttonText,
    cancelText = false,
    confirmText = 'Confirm',
    confirmMessage = <b className={style.confirmButton}>{confirmText}</b>,
    successMessage,
    hidden = false,
    callback = () => {},
    tagName = 'div',
    hideOnFinish = false,
    newStyle = false,
    textButton = false,
    className = undefined,
}) => {
    const { addToast } = useToast()
    const [showConfirm, setShowConfirm] = useState(false)
    const [actionCompleted, setActionCompleted] = useState(false)
    const [startHiding, setStartHiding] = useState(false)
    const [isHidden, setIsHidden] = useState(hidden)
    const [error, setError] = useState(false)
    const [loading, setLoading] = useState(false)

    const ref = useRef(null)

    const onBodyClick = (e) => {
        if (
            ref.current &&
            e.target !== ref.current &&
            !ref.current.contains(e.target) &&
            showConfirm
        ) {
            setShowConfirm(false)
        }
    }

    useEffect(() => {
        document.body.addEventListener('click', onBodyClick)

        return () => {
            document.body.removeEventListener('click', onBodyClick)
        }
    }, [showConfirm])

    const onError = (response) => {
        if (response) {
            addToast({
                msg: response.msg || response.error || JSON.stringify(response, null, 2),
                red: true,
                icon: 'warning',
                timeout: 10000,
            })
        }
        setError(true)
        setLoading(false)
    }

    const onClick = async (e) => {
        if (loading || actionCompleted) {
            return
        }

        if (showConfirm) {
            setLoading(true)
            try {
                const response = await action()
                callback && callback(response)
                setLoading(false)
                setActionCompleted(true)
                setTimeout(() => setStartHiding(true), 100)
                setTimeout(() => setIsHidden(true), 200)
            } catch (error) {
                onError(error)
            }
        } else {
            setShowConfirm(true)
        }
    }

    const onCancel = (e) => {
        e.stopPropagation()
        setShowConfirm(false)
    }

    const renderButtonContent = () => {
        if (error) {
            return <div>Error!</div>
        }
        if (actionCompleted) {
            return <div>{successMessage || 'Success!'}</div>
        }
        if (loading) {
            return <LoadingDots />
        }
        if (showConfirm) {
            return <div>{confirmMessage}</div>
        }
        return false
    }

    if (isHidden) {
        return null
    }

    const showCancelButton = cancelText !== false && showConfirm && !actionCompleted && !loading
    const classString = clsx(
        className,
        showConfirm && style.warning,
        startHiding && hideOnFinish && style.animateOut,
        loading && style.isLoading,
        actionCompleted && style.done,
    )

    const buttonContent = renderButtonContent()
    const ButtonType = newStyle || textButton ? Button : 'button'
    const buttonName = textButton ? 'text' : undefined
    const cancelButtonName = newStyle ? newStyle.toString() : buttonName
    return React.createElement(
        tagName,
        { ref: ref, className: style.root },
        <>
            <ButtonType
                key={1}
                name={buttonName}
                style={buttonContent ? { display: 'none' } : {}}
                onClick={onClick}
                className={classString}>
                {buttonText}
            </ButtonType>
            <ButtonType
                key={2}
                style={buttonContent ? {} : { display: 'none' }}
                name={buttonName}
                onClick={onClick}
                className={classString}>
                {buttonContent}
            </ButtonType>
            <ButtonType
                key={3}
                className={style.cancelButton}
                style={showCancelButton ? {} : { display: 'none' }}
                onClick={onCancel}
                name={cancelButtonName}>
                {cancelText || <Icon name="close" />}
            </ButtonType>
        </>,
    )
}

SafeButton.propTypes = {
    action: PropTypes.func,
    buttonText: PropTypes.any,
    cancelText: PropTypes.any,
    confirmMessage: PropTypes.any,
    hidden: PropTypes.bool,
    callback: PropTypes.func,
}

export default SafeButton
